import { call, select, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import JourneyOutcome from '../../../../Data/JourneyOutcome';
import JourneyOutcomeDetail from '../../../../Data/JourneyOutcomeDetail';
import { FALL_THROUGH_REASONS } from '../../../../Components/ClientApi/clientApiJourney.constants';
import { incrementMetric } from '../../../../Services/Datadog';
import { getErrorTypeTag } from '../clientApiError.helpers';
import reportJourneyOutcomeOnce from '../reportJourneyOutcomeOnce';
import { captureWithSubmissionId } from '../SubmitClientApiBooking/captureWithSubmissionId';

// The early endings that have no saga of their own to report the outcome. Each
// maps to the outcome and detail Outreach reads when the journey ends here.
export const FALL_THROUGH_OUTCOMES = {
  [FALL_THROUGH_REASONS.NO_SLOTS]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.NO_SLOTS,
  },
  [FALL_THROUGH_REASONS.SLOTS_LOOKUP_FAILED]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.SLOTS_LOOKUP_FAILED,
  },
  [FALL_THROUGH_REASONS.NO_LOCATIONS]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.NO_LOCATIONS,
  },
  [FALL_THROUGH_REASONS.LOCATION_LOOKUP_FAILED]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.LOCATION_LOOKUP_FAILED,
  },
  [FALL_THROUGH_REASONS.SOLAR_LOAD_FAILED]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.SOLAR_LOAD_FAILED,
  },
  [FALL_THROUGH_REASONS.SOLAR_VERIFY_FAILED]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.SOLAR_VERIFY_FAILED,
  },
  [FALL_THROUGH_REASONS.NO_SOLAR_POTENTIAL]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.NO_SOLAR_POTENTIAL,
  },
  // Outreach ended the journey itself (lead status FAILED or ABANDONED) and
  // still wants the outcome reported, with the same detail the review screen
  // reports for a terminal lead (agreed with Rhino, GOLD-2689).
  [FALL_THROUGH_REASONS.LEAD_STATUS_FAILED]: {
    outcome: JourneyOutcome.GOAL_UNMET,
    outcomeDetail: JourneyOutcomeDetail.LEAD_ALREADY_RELEASED,
  },
};

export const DEFAULT_FALL_THROUGH_OUTCOME = {
  outcome: JourneyOutcome.TIMEOUT,
  outcomeDetail: JourneyOutcomeDetail.TIMED_OUT,
};

// These endings fall through the shared hook too, but each already reports its
// own outcome from its own saga, so reporting here would be a second send.
export const REASONS_REPORTED_BY_OWN_SAGA = [
  FALL_THROUGH_REASONS.SUBSCRIPTION_MISMATCH,
  FALL_THROUGH_REASONS.BOOKING_NOT_CONFIRMABLE,
  FALL_THROUGH_REASONS.BOOKING_FAILED,
];

/**
 * Reports the journey outcome for an early ending that the shared fall-through
 * hook drives, so Outreach always learns the outcome. A reason whose own saga
 * already reports is skipped here; any other reason uses its mapped outcome, or
 * the default timeout outcome when it has none. A failed send is swallowed so
 * the form still advances; the once-only guard in reportJourneyOutcomeOnce stops
 * a second send.
 */
export function* reportFallThroughOutcomeWorker({ reason }) {
  if (REASONS_REPORTED_BY_OWN_SAGA.includes(reason)) {
    return;
  }

  const outcome = FALL_THROUGH_OUTCOMES[reason] || DEFAULT_FALL_THROUGH_OUTCOME;

  try {
    yield call(reportJourneyOutcomeOnce, outcome);
  } catch (error) {
    const submissionId = yield select((state) => state.submission?.id);
    incrementMetric({
      name: 'reportFallThroughOutcomeError',
      tags: { reason, type: getErrorTypeTag(error) },
    });
    captureWithSubmissionId({ error, submissionId });
  }
}

export default function* reportFallThroughOutcomeWatcher() {
  yield takeEvery(
    ActionTypes.REPORT_CLIENT_API_FALL_THROUGH_OUTCOME,
    reportFallThroughOutcomeWorker
  );
}
